// Hàm thuần cho trang web Boss: định dạng thời gian theo giờ Việt Nam, nhãn trạng thái tiếng Việt, chia_deu, gộp tham số.
// Không đụng DOM để unit test được trong Node.
import type { JobStatus, ServerRunStatus } from "./types";

const TZ = "Asia/Ho_Chi_Minh";

function parseIso(iso: string | null | undefined): Date | null {
  if (!iso || typeof iso !== "string") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // "2026-02-30" JS tự nhảy sang tháng 3 → coi là không hợp lệ.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) {
    const y = Number(m[1]), mo = Number(m[2]), da = Number(m[3]);
    const maxDay = new Date(Date.UTC(y, mo, 0)).getUTCDate();
    if (mo < 1 || mo > 12 || da < 1 || da > maxDay) return null;
  }
  return d;
}

const dtf = new Intl.DateTimeFormat("vi-VN", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

function parts(d: Date): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of dtf.formatToParts(d)) out[p.type] = p.value;
  // Một số bản ICU trả "24" cho 0 giờ khi hour12=false.
  if (out.hour === "24") out.hour = "00";
  return out;
}

/** ISO → "dd/MM/yyyy HH:mm" giờ Việt Nam; không hợp lệ → "". */
export function fmtTime(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) return "";
  const p = parts(d);
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

/** ISO → "HH:mm:ss" giờ Việt Nam (cho nhật ký bước). */
export function fmtClock(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) return "";
  const p = parts(d);
  return `${p.hour}:${p.minute}:${p.second}`;
}

/** ISO → "yyyy-MM-dd" theo giờ Việt Nam (cho ô input date). */
export function isoDateVN(d: Date): string {
  const p = parts(d);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Thời lượng (mili giây) → chữ Việt cho người đọc: "0,3 giây", "1,5 giây", "2 phút 05 giây" (V7: bỏ "ms"/"s"/"p"). */
export function fmtMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0 giây";
  if (ms < 100) return "dưới 0,1 giây";
  if (ms < 59_950) {
    const g = Math.round(ms / 100) / 10; // một chữ số thập phân
    return `${Number.isInteger(g) ? String(g) : g.toFixed(1).replace(".", ",")} giây`;
  }
  const tong = Math.round(ms / 1000);
  const m = Math.floor(tong / 60), s = tong % 60;
  return `${m} phút ${String(s).padStart(2, "0")} giây`;
}

/** Ngày "yyyy-MM-dd" (khoá ngày của Thống kê / So sánh) → "dd/MM" cho trục biểu đồ; chuỗi khác giữ nguyên. */
export function fmtNgayNgan(d: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d ?? "");
  return m ? `${m[3]}/${m[2]}` : (d ?? "");
}
/** Ngày "yyyy-MM-dd" → "dd/MM/yyyy" (bảng số liệu theo ngày); chuỗi khác giữ nguyên. */
export function fmtNgay(d: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d ?? "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (d ?? "");
}

/** "5 phút trước" — now cho test. */
export function fmtAgo(iso: string | null | undefined, now = Date.now()): string {
  const d = parseIso(iso);
  if (!d) return "chưa từng";
  const s = Math.max(0, Math.round((now - d.getTime()) / 1000));
  if (s < 45) return "vừa xong";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} phút trước`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} giờ trước`;
  return `${Math.round(h / 24)} ngày trước`;
}

// ---- Nhãn trạng thái. Record<JobStatus,…> để TS bắt thiếu khi hợp đồng thêm trạng thái; test kiểm cả runtime.
const JOB_LABEL: Record<JobStatus, string> = {
  cho_canary: "Đang chạy 1 bot trước",
  canary_hoi: "Chờ bạn quyết phát tiếp",
  dang_chay: "Đang chạy",
  cho_duyet: "Chờ duyệt",
  xong: "Xong",
  co_loi: "Có lỗi",
  canary_that_bai: "Bot chạy trước bị lỗi",
  da_huy: "Đã huỷ",
};
export const JOB_STATUSES = Object.keys(JOB_LABEL) as JobStatus[];

const RUN_LABEL: Record<ServerRunStatus, string> = {
  running: "Đang chạy",
  paused_approval: "Chờ duyệt",
  paused_otp: "Chờ nhập OTP",
  done: "Xong",
  halted: "Bị chặn / từ chối",
  stopped: "Đã dừng tay",
  failed: "Lỗi",
  cho_gian_cach: "Chờ giãn cách",
  cho_den_luot: "Chờ đến lượt",
  mat_lien_lac: "Mất liên lạc",
  khong_ro: "Không rõ kết quả",
  het_gio: "Hết giờ",
  bo_qua: "Bỏ qua",
  da_huy: "Đã huỷ",
};
// Danh sách trạng thái cho bộ lọc Lịch sử — đủ mọi trạng thái có nhãn, kể cả "Đã huỷ" (02/10/2026: trước bị bỏ nên không lọc ra được
// lần chạy đã huỷ; máy chủ đã nhận ?status=da_huy từ trước).
export const RUN_STATUSES = Object.keys(RUN_LABEL) as ServerRunStatus[];

// L11 (rà soát 05/10): bộ lọc Lịch sử mặc định 5 NHÓM thay 14 trạng thái kỹ thuật; "Lọc chi tiết" mới hiện từng trạng thái.
// giaTri = danh sách mã cách dấu phẩy — máy chủ nhận ?status=a,b,c (history.ts). Mọi trạng thái thuộc đúng một nhóm (test kiểm).
export interface NhomLocRun { nhan: string; title: string; trangThai: ServerRunStatus[]; giaTri: string }
const nhomLoc = (nhan: string, title: string, trangThai: ServerRunStatus[]): NhomLocRun => ({ nhan, title, trangThai, giaTri: trangThai.join(",") });
export const NHOM_LOC_RUN: readonly NhomLocRun[] = [
  nhomLoc("Đang chạy / xếp hàng", "Bot đang chạy, hoặc chưa bắt đầu vì đang chờ giãn cách / chờ đến lượt", ["running", "cho_gian_cach", "cho_den_luot"]),
  nhomLoc("Chờ duyệt", "Bot dừng chờ bạn duyệt bước gửi/đăng/xoá hoặc nhập mã OTP", ["paused_approval", "paused_otp"]),
  nhomLoc("Xong", "Bot đã chạy xong", ["done"]),
  nhomLoc("Lỗi / bị chặn", "Lỗi, bị trang chặn hoặc bạn từ chối, hết giờ, mất liên lạc, không rõ kết quả", ["failed", "halted", "het_gio", "mat_lien_lac", "khong_ro"]),
  nhomLoc("Đã dừng / huỷ / bỏ qua", "Bạn đã dừng, lệnh bị huỷ, hoặc bot bị bỏ qua khi phát", ["stopped", "da_huy", "bo_qua"]),
];

// ---- So sánh Boss: màu cố định theo Boss (02/10/2026). Trước đây màu lấy theo THỨ HẠNG trong từng bảng nên cùng một Boss mang màu khác
// nhau giữa bảng "Tất cả Boss" và bảng nhóm. Nay băm mã Boss (FNV-1a 32 bit) → một màu trong bảng; dùng chung mọi bảng/biểu đồ.
export const BANG_MAU_BOSS: readonly string[] = ["#2563eb", "#059669", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#be185d", "#4d7c0f", "#c2410c", "#0f766e", "#9333ea", "#475569"];
export function mauBoss(ownerId: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < ownerId.length; i++) { h ^= ownerId.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return BANG_MAU_BOSS[h % BANG_MAU_BOSS.length]!;
}

export function jobStatusText(s: string): string {
  return (JOB_LABEL as Record<string, string>)[s] ?? (s ?? "");
}
export function runStatusText(s: string): string {
  return (RUN_LABEL as Record<string, string>)[s] ?? (s ?? "");
}

// ---- L-38/L-40: lần chạy "done" mà bot báo cảnh báo (máy chủ ghi lý do bắt đầu bằng ⚠ — jobs.ts onBotEvent) → vàng, không "Xong" trơn.
export const DAU_CANH_BAO = "⚠";
export function runCoCanhBao(r: { status: string; reason?: string | null }): boolean {
  return r.status === "done" && typeof r.reason === "string" && r.reason.startsWith(DAU_CANH_BAO);
}
export function jobCoCanhBao(j: { status: string; reason?: string | null }): boolean {
  return j.status === "xong" && typeof j.reason === "string" && j.reason.startsWith(DAU_CANH_BAO);
}
/** Nhãn + nhóm màu để hiện huy hiệu trạng thái lần chạy (có tính cảnh báo). */
export function runNhanHien(r: { status: string; reason?: string | null }): { text: string; kind: ReturnType<typeof runStatusKind> } {
  return runCoCanhBao(r) ? { text: "Xong — có cảnh báo", kind: "warn" } : { text: runStatusText(r.status), kind: runStatusKind(r.status) };
}
export function jobNhanHien(j: { status: string; reason?: string | null }): { text: string; kind: ReturnType<typeof jobStatusKind> } {
  return jobCoCanhBao(j) ? { text: "Xong — có cảnh báo", kind: "warn" } : { text: jobStatusText(j.status), kind: jobStatusKind(j.status) };
}
/** Giờ dự kiến bắt đầu (sau giãn cách). Lần chạy đã huỷ không bao giờ bắt đầu → không hiện giờ dự kiến (L-39). */
export function fmtDuKien(r: { status: string; plannedAt?: string | null }): string {
  return r.status === "da_huy" ? "" : fmtTime(r.plannedAt);
}
/** Lớp CSS theo nhóm màu: running/ok/err/warn/muted. */
export function runStatusKind(s: string): "running" | "ok" | "err" | "warn" | "muted" {
  switch (s) {
    case "running": return "running";
    case "done": return "ok";
    case "failed": case "halted": case "mat_lien_lac": case "het_gio": return "err";
    case "paused_approval": case "paused_otp": case "khong_ro": return "warn";
    default: return "muted";
  }
}
export function jobStatusKind(s: string): "running" | "ok" | "err" | "warn" | "muted" {
  switch (s) {
    case "dang_chay": case "cho_canary": return "running";
    case "xong": return "ok";
    case "co_loi": case "canary_that_bai": return "err";
    case "cho_duyet": case "canary_hoi": return "warn";
    default: return "muted";
  }
}
/** Lệnh/lần chạy còn đang sống (cho phép Huỷ/Dừng). */
export function jobAlive(s: string): boolean {
  return s === "cho_canary" || s === "canary_hoi" || s === "dang_chay" || s === "cho_duyet";
}
export function runAlive(s: string): boolean {
  return s === "running" || s === "paused_approval" || s === "paused_otp" || s === "cho_gian_cach" || s === "cho_den_luot" || s === "mat_lien_lac" || s === "khong_ro";
}

const STEP_LABEL: Record<string, string> = { ok: "đạt", fail: "lỗi", skip: "bỏ qua", halt: "dừng", draft: "chỉ ghi lại" };
export function stepStatusText(s: string): string {
  return STEP_LABEL[s] ?? s;
}

// ---- Tooltip (title) giải thích nghĩa của từng huy hiệu trạng thái.
const JOB_TITLE: Record<JobStatus, string> = {
  cho_canary: "Đang chạy 1 bot trước; xong không lỗi máy chủ mới phát cho các bot còn lại",
  canary_hoi: "Bot chạy trước bị trang web từ chối — máy chủ đang hỏi bạn có phát tiếp cho các bot còn lại không",
  dang_chay: "Ít nhất một bot đang chạy hoặc đang chờ tới lượt",
  cho_duyet: "Có bot đang dừng ở bước rủi ro cao chờ bạn duyệt hoặc nhập mã OTP",
  xong: "Mọi bot đã chạy xong không lỗi",
  co_loi: "Đã kết thúc nhưng có bot lỗi, bị trang chặn, hết giờ hoặc mất liên lạc",
  canary_that_bai: "Bot chạy trước lỗi nên máy chủ không phát cho các bot còn lại",
  da_huy: "Lệnh đã bị huỷ (bởi bạn, DỪNG TẤT CẢ, hoặc bạn không cho phát tiếp sau bot chạy trước)",
};
export function jobStatusTitle(s: string): string {
  return (JOB_TITLE as Record<string, string>)[s] ?? "Trạng thái lệnh";
}
const RUN_TITLE: Record<ServerRunStatus, string> = {
  running: "Bot đang thực hiện các bước của kịch bản",
  paused_approval: "Bot dừng ở bước rủi ro cao (gửi/đăng/xoá), chờ bạn Duyệt hoặc Từ chối",
  paused_otp: "Bot chờ bạn nhập mã OTP hoặc giá trị xác nhận rồi mới chạy tiếp",
  done: "Bot đã chạy xong mọi bước, trang web đã nhận",
  halted: "Bot dừng vì trang web không nhận việc vừa làm (bị chặn), hoặc bạn bấm Từ chối ở bước chờ duyệt",
  stopped: "Bạn hoặc DỪNG TẤT CẢ đã dừng lần chạy này giữa chừng",
  failed: "Lần chạy lỗi (không tìm thấy phần tử, bot báo lỗi…) — xem cột lý do",
  cho_gian_cach: "Chưa bắt đầu: đang chờ hết thời gian giãn cách ngẫu nhiên",
  cho_den_luot: "Chưa bắt đầu: bot đang bận lần chạy khác hoặc chờ bot chạy trước xong",
  mat_lien_lac: "Bot ngắt kết nối giữa chừng, máy chủ không biết kết quả",
  khong_ro: "Bot nối lại nhưng không báo được kết quả lần chạy này",
  het_gio: "Quá thời gian hết giờ của lệnh mà chưa xong nên máy chủ dừng",
  bo_qua: "Bot bị bỏ qua khi phát (ngoại tuyến, chưa được phép chạy thật, hết hạn mức…) — xem cột lý do",
  da_huy: "Lần chạy bị huỷ trước khi xong (bạn huỷ lệnh hoặc DỪNG TẤT CẢ) — xem cột lý do",
};
export function runStatusTitle(s: string): string {
  return (RUN_TITLE as Record<string, string>)[s] ?? "Trạng thái lần chạy";
}
const STEP_TITLE: Record<string, string> = {
  ok: "Bước chạy đạt", fail: "Bước lỗi — xem ghi chú", skip: "Bước bị bỏ qua (điều kiện không khớp hoặc không người duyệt)",
  halt: "Bước làm dừng lần chạy (trang web không nhận hoặc bị từ chối)", draft: "Chạy thử: bước rủi ro cao chỉ ghi lại, không làm thật",
};
export function stepStatusTitle(s: string): string {
  return STEP_TITLE[s] ?? "Kết quả bước";
}

// ---- Loại bước (kindOf trong runner) và nấc tìm phần tử (Rung.t) → chữ Việt cho nhật ký bước; mã lạ hiện nguyên.
const STEP_KIND: Record<string, string> = {
  do: "thao tác", extract: "trích dữ liệu", filter: "lọc", classify: "phân loại (AI)", template: "điền mẫu", generate: "soạn (AI)",
  paginate: "lật trang", wait_for: "chờ điều kiện", wait_file: "chờ tệp", steps: "nhóm bước", wait: "chờ",
  chan_doan: "chẩn đoán", gac_cong: "gác cổng", heal: "tự tìm lại nút", interrupt: "ngắt", kiem_noi_dung: "kiểm nội dung", note: "ghi chú",
  click: "bấm", type: "gõ", select: "chọn", press: "phím", navigate: "mở trang", scroll: "cuộn", expect: "kiểm",
};
export function stepKindText(k: string): string {
  return STEP_KIND[k] ?? (k ?? "");
}
const RUNG_TEXT: Record<string, string> = { css: "theo mã trang", text: "theo chữ", xpath: "theo đường dẫn trang", geometry: "theo vị trí", model: "nhờ AI" };
export function rungText(t: string | null | undefined): string {
  if (!t) return "";
  return RUNG_TEXT[t] ?? t;
}

// ---- Chế độ chạy (docs/thuat-ngu.md): mã draft/dry/live chỉ còn trong API/YAML, người dùng thấy chữ Việt.
// "Chỉ dò trang" (dry) chỉ còn là lựa chọn trong chế độ kỹ thuật (Super) — lịch sử cũ có dry vẫn hiện nhãn này.
const MODE_LABEL: Record<string, string> = { draft: "Chạy thử (cũ)", dry: "Chỉ dò trang", live: "Chạy thật" };
export function modeText(m: string): string {
  return MODE_LABEL[m] ?? (m ?? "");
}
/** Lớp CSS cho huy hiệu chế độ: chạy thật phải nổi hơn hẳn chạy thử/chỉ định vị (rủi ro khác nhau). */
export function modeKind(m: string): "live" | "draft" | "dry" | "" {
  return m === "live" || m === "draft" || m === "dry" ? m : "";
}
/** Huy hiệu chế độ đầy đủ lớp: "tag live" / "tag draft" / "tag dry". */
export function modeTagClass(m: string): string {
  const k = modeKind(m);
  return k ? `tag ${k}` : "tag";
}

/** Vạch trục dọc cho biểu đồ đếm: bước "đẹp" (1, 2, 5 × 10ⁿ), mọi nhãn là số nguyên khác nhau, đỉnh trục ≥ giá trị lớn nhất.
 *  Trả về top (giá trị ứng với vạch trên cùng — dùng làm thang cho cột) và danh sách vạch từ 0 tới top. */
export function yTicks(max: number, target = 4): { top: number; ticks: number[] } {
  const m = Number.isFinite(max) && max > 0 ? Math.ceil(max) : 1;
  const raw = m / Math.max(1, target);
  const mu = 10 ** Math.floor(Math.log10(Math.max(1, raw)));
  const step = Math.max(1, [1, 2, 5, 10].map((f) => f * mu).find((s) => s >= raw) ?? 10 * mu);
  const top = Math.ceil(m / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  return { top, ticks };
}

/** Giải thích ngắn cho tooltip huy hiệu chế độ. */
export function modeTitle(m: string): string {
  switch (m) {
    case "draft": return "Chạy thử: bot làm mọi bước không rủi ro, bước rủi ro cao chỉ ghi lại — không gửi/đăng/xoá gì";
    case "dry": return "Chỉ dò trang: bot chỉ tìm nút/ô trên trang, không bấm, không gõ";
    case "live": return "Chạy thật: bot thao tác thật trên trang web";
    default: return "Chế độ chạy";
  }
}

// ---- chia_deu
export function parseListText(text: string): string[] {
  return text.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
}

/** Phần dư rải cho các bot đầu, mỗi bot thêm một phần tử (ước lượng hiển thị; server gói C quyết định cách chia thật). */
export function splitCounts(n: number, m: number): number[] {
  if (m <= 0) return [];
  const base = Math.floor(n / m), extra = n - base * m;
  return Array.from({ length: m }, (_, i) => base + (i < extra ? 1 : 0));
}

export function splitList(list: string[], botIds: string[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!botIds.length) return out;
  const counts = splitCounts(list.length, botIds.length);
  let i = 0;
  botIds.forEach((id, idx) => {
    const n = counts[idx] ?? 0;
    out[id] = list.slice(i, i + n);
    i += n;
  });
  return out;
}

export function chiaDeuText(n: number, m: number): string {
  if (n <= 0) return "Chưa có phần tử nào";
  if (m <= 0) return `${n} phần tử — chưa tick bot nào`;
  const counts = splitCounts(n, m);
  const same = counts.every((c) => c === counts[0]);
  const idle = counts.filter((c) => c === 0).length;
  const shape = same ? `${counts[0]}` : counts.join("/");
  return `${n} phần tử, chia cho ${m} bot ≈ ${shape} mỗi bot${idle ? ` (${idle} bot không có việc)` : ""}`;
}

// ---- gộp tham số theo bot: chung ← ghi đè bởi bảng riêng (bỏ ô rỗng)
export function mergeParamsByBot(
  common: Record<string, string>,
  byBot: Record<string, Record<string, string>> | undefined,
  botIds: string[],
): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const id of botIds) {
    const own = byBot?.[id] ?? {};
    const merged: Record<string, string> = { ...common };
    for (const [k, v] of Object.entries(own)) if (v !== "" && v !== undefined && v !== null) merged[k] = v;
    out[id] = merged;
  }
  return out;
}

// ---- gộp "đang làm gì" của bot
export interface BotLike {
  online: boolean;
  status?: { teaching?: boolean; run?: { bundleName: string; status: string; stepId?: string } };
  current?: { runKey: string; bundleName: string; status: string; stepId?: string };
}
export function gopTrangThaiBot(b: BotLike): { text: string; kind: "running" | "pending" | "teaching" | "idle" | "offline" } {
  if (!b.online) return { text: b.current ? "mất liên lạc" : "ngoại tuyến", kind: "offline" };
  // current do server tính từ bảng runs (trạng thái sống); bot.status.run chỉ là dự phòng và bỏ qua khi run đã xong (lỗi E/L2).
  const song = (s: string) => s === "running" || s.startsWith("paused");
  const cur = b.current ?? (b.status?.run && song(b.status.run.status) ? b.status.run : undefined);
  if (cur) {
    if (cur.status === "paused_approval") return { text: `${cur.bundleName} · chờ duyệt`, kind: "pending" };
    if (cur.status === "paused_otp") return { text: `${cur.bundleName} · chờ mã OTP`, kind: "pending" };
    return { text: cur.stepId ? `${cur.bundleName} · bước ${cur.stepId}` : cur.bundleName, kind: "running" };
  }
  if (b.status?.teaching) return { text: "đang dạy", kind: "teaching" };
  return { text: "rảnh", kind: "idle" };
}

export function pct(rate: number): string {
  if (!Number.isFinite(rate)) return "—";
  return `${Math.round(rate * 100)}%`;
}

// ---- N9 (rà soát 05/10): mã bước / mục nội bộ → chữ người đọc. Bảng tên do máy chủ trả (GET /api/ten-buoc — ten-kb.ts gọi gộp).
export interface TenBuocKb { so?: number; ten: string }
/** Tên bước để hiện: theo sơ đồ của kịch bản ("Bước 2 · Bình luận các bài tìm được") → tên chung (bỏ đuôi _n của bước lặp lại)
 *  → mã bỏ dấu gạch dưới. Không bao giờ trả mã nguyên dạng máy. */
export function hienTenBuoc(stepId: string, kb: Record<string, TenBuocKb> | undefined, chung: Record<string, string> | undefined): string {
  if (!stepId) return "";
  const k = kb && Object.prototype.hasOwnProperty.call(kb, stepId) ? kb[stepId] : undefined;
  if (k) return k.so ? `Bước ${k.so} · ${k.ten}` : k.ten;
  const goc = stepId.replace(/_\d+$/, "");
  const c = chung && (Object.prototype.hasOwnProperty.call(chung, stepId) ? chung[stepId] : Object.prototype.hasOwnProperty.call(chung, goc) ? chung[goc] : undefined);
  if (c) return c;
  return stepId.replace(/_+/g, " ").trim();
}

/** Thay mã bước nằm trong câu lý do lỗi/dừng ("lay_bai: không định vị…", "Bước “lay_bai” không…", "bước dang_binh_luan") bằng tên bước.
 *  `tra(id)` trả tên khi biết mã đó, null khi không — chữ thường trong câu không bị đụng. */
export function thayMaBuoc(text: string | null | undefined, tra: (id: string) => string | null): string {
  if (!text) return "";
  const bo = (ten: string) => ten.replace(/^Bước \d+ · /, "");
  // Mã bước đứng đầu câu ("lay_bai: …", "⚠ lay_bai: …") hoặc ngay sau ": " (lý do gộp của lệnh: "1 lần chạy lỗi: lay_bai: …").
  let out = text.replace(/(^|: |⚠\s*)([A-Za-z][A-Za-z0-9_]*): /g, (m, dau: string, id: string) => {
    const t = tra(id);
    return t ? `${dau}${t}: ` : m;
  });
  // Nháy cong “x” / ‘x’ và nháy THẲNG 'x' / "x" (plugin ghi "0 mục ở bước 'lay_bl_video_kenh'"): chỉ thay khi biết mã đó là bước.
  out = out.replace(/([Bb]ước) [“"'‘]([A-Za-z][A-Za-z0-9_]*)[”"'’]/g, (m, chu: string, id: string) => {
    const t = tra(id);
    return t ? `${chu} “${bo(t)}”` : m;
  });
  out = out.replace(/([Bb]ước) ([a-z][a-z0-9]*_[a-z0-9_]*)\b/g, (m, chu: string, id: string) => {
    const t = tra(id);
    return t ? `${chu} “${bo(t)}”` : m;
  });
  return out;
}

/**
 * L-89: khoảng nghỉ giữa hai kịch bản của lần CHẠY THỬ được máy chủ rút ×0,1 (ly_do "Nháp rút còn 3 giây (chạy thật nghỉ 30 giây)") — hiện cho người dùng hiểu vì sao nghỉ ngắn hơn đã cài.
 * Trả đoạn nối vào cuối câu ("" khi không phải lần chạy thử / câu lạ).
 */
export function chuNghiNhap(lyDo: string | null | undefined): string {
  const m = /^Nháp rút còn (.+?) \(chạy thật nghỉ (.+)\)$/.exec(lyDo ?? "");
  return m ? ` (Nháp rút còn ${m[1]} — chạy thật nghỉ ${m[2]})` : "";
}

/** Mục của danh sách mà bot ghi bằng mã nội bộ ("i0", "i12") — không có nghĩa với người dùng, không hiện. */
export function laMucNoiBo(item: string | null | undefined): boolean {
  return typeof item === "string" && /^i\d+$/.test(item);
}
/** Tên bot, thêm " (tắt)" khi bot không trực tuyến — nhãn nút chọn bot (Kho video). */
export const nhanBotTat = (b: { name: string; online: boolean }): string => b.name + (b.online ? "" : " (tắt)");
